import { InputCurrency } from "@finanedi.es/storybook"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { ImpuestoInsertFacturaOcrCommandProps } from "api/Commands/ImpuestoInsertFacturaOcrCommand"
import { RetencionInsertFacturaOcrCommandProps } from "api/Commands/RetencionInsertFacturaOcrCommandProps"
import { useCurrency } from "common/hooks/useCurrency"
import { DEFAULT_CURRENCY_ABREVIATURE } from "features/Contactos/constants/index"
import { ErrorMessage, FieldArray } from "formik"
import { Dropdown } from "primereact/dropdown"
import { InputNumber } from "primereact/inputnumber"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { precisionNumber } from "utils/precisionNumber"

const ImporteForm = ({
  values,
  errors,
  touched,
  handleBlur,
  setFieldValue,
}: $TSMigrationFixMe) => {
  const { t } = useTranslation()
  const { currencyListFormatDropdown } = useCurrency()
  const { divisa = DEFAULT_CURRENCY_ABREVIATURE } = values
  const findImpuesto = (lista: $TSMigrationFixMe, impuestoValue = 21) =>
    lista?.find(
      ({ value }: $TSMigrationFixMe) => value?.valor === impuestoValue
    )?.value ?? {
      id: undefined,
      valor: undefined,
    }
  const impuestoExento = findImpuesto(values.listaImpuestos)

  useEffect(() => {
    const totalBaseImponible = values.impuestos?.reduce(
      (
        acc: number,
        {
          baseImponible,
        }: {
          baseImponible: ImpuestoInsertFacturaOcrCommandProps["baseImponible"]
        }
      ) => acc + baseImponible,
      0
    )
    setFieldValue("totalRetencion", precisionNumber(totalBaseImponible))
  }, [setFieldValue, values.impuestos])

  useEffect(() => {
    values.listaImpuestos?.length &&
      values.impuestos?.forEach(
        (impuesto: $TSMigrationFixMe, index: number) => {
          const tax = findImpuesto(
            values.listaImpuestos,
            impuesto.porcentaje?.valor ?? 21
          )
          setFieldValue(`impuestos.${index}.porcentaje`, tax)
          !impuesto?.total &&
            setFieldValue(
              `impuestos.${index}.total`,
              precisionNumber((impuesto.baseImponible * tax?.valor) / 100)
            )
        }
      )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.listaImpuestos?.length])

  useEffect(() => {
    const isValid = currencyListFormatDropdown.some(
      ({ value }) => value === divisa
    )
    setFieldValue("divisa", isValid ? divisa : DEFAULT_CURRENCY_ABREVIATURE)
  }, [setFieldValue, divisa, currencyListFormatDropdown])

  return (
    <>
      <h2 className='tw-text-left tw-text-neutral-75 tw-font-medium tw-text-base'>
        {t("ocr.amount.taxes.title")}
      </h2>
      <FieldArray name='impuestos'>
        {({ remove: removeImpuesto, push: pushImpuesto }) => (
          <div className='tw-bg-background tw-pb-6 tw-mb-6'>
            <div className='tw-grid tw-grid-cols-3 tw-gap-2 tw-bg-white tw-px-4 tw-mb-4'>
              <div className='tw-text-left tw-text-text-50 tw-text-base tw-font-medium tw-p-2'>
                {t("ocr.amount.taxes.base")}
              </div>
              <div className='tw-text-left tw-text-text-50 tw-text-base tw-font-medium tw-p-2'>
                {t("ocr.amount.taxes.percentage")}
              </div>
              <div className='tw-text-left tw-text-text-50 tw-text-base tw-font-medium tw-p-2'>
                {t("ocr.amount.taxes.total")}
              </div>
            </div>
            {values.impuestos?.length > 0 &&
              values.impuestos?.map(
                (
                  _impuesto: ImpuestoInsertFacturaOcrCommandProps,
                  index: number
                ) => (
                  <div
                    className='tw-bg-background tw-px-4 tw-pb-4'
                    key={index} // eslint-disable-line
                  >
                    <div className='tw-grid tw-grid-cols-3 tw-gap-2 tw-bg-white tw-p-2'>
                      <div>
                        <InputCurrency
                          className={`tw-w-full ${
                            touched?.impuestos?.[index]?.baseImponible &&
                            errors?.impuestos?.[index]?.baseImponible
                              ? "!tw-border-danger"
                              : ""
                          }`}
                          value={values.impuestos[index].baseImponible}
                          onChange={(value) => {
                            const inputValue = Number.isNaN(value)
                              ? null
                              : value
                            inputValue &&
                              setFieldValue(
                                `impuestos.${index}.total`,
                                precisionNumber(
                                  (inputValue *
                                    values.impuestos[index].porcentaje?.valor) /
                                    100
                                )
                              )
                            setFieldValue(
                              `impuestos.${index}.baseImponible`,
                              inputValue
                            )
                          }}
                          placeholder='0,00'
                          suffix='€'
                        />
                        <ErrorMessage
                          name={`impuestos.${index}.baseImponible`}
                          component='div'
                          className='tw-text-danger tw-text-left'
                        />
                      </div>
                      <div>
                        <Dropdown
                          className={`tw-w-full tw-rounded-md tw-border-neutral-25 ${
                            touched?.impuestos?.[index]?.porcentaje?.id &&
                            errors?.impuestos?.[index]?.porcentaje?.id
                              ? "!tw-border-danger"
                              : ""
                          }`}
                          id='impuestosPorcentaje'
                          name='impuestosPorcentaje'
                          optionLabel='value.valorLabel'
                          options={values.listaImpuestos}
                          value={values.impuestos[index].porcentaje}
                          valueTemplate={
                            values.impuestos[index].porcentaje?.valorLabel
                          }
                          onChange={({ value }) => {
                            setFieldValue(
                              `impuestos.${index}.porcentaje`,
                              value
                            )
                            setFieldValue(
                              `impuestos.${index}.total`,
                              precisionNumber(
                                (values.impuestos[index].baseImponible *
                                  value?.valor) /
                                  100
                              )
                            )
                          }}
                          placeholder={`${t("ocr.placeholderPorcentaje")}`}
                          emptyFilterMessage={t("ocr.emptyMsg")}
                          emptyMessage={t("ocr.emptyMsg")}
                        />
                        <ErrorMessage
                          name={`impuestos.${index}.porcentaje.id`}
                          component='div'
                          className='tw-text-danger tw-text-left'
                        />
                      </div>
                      <div>
                        <div className='tw-flex'>
                          <InputCurrency
                            className={`tw-w-full ${
                              touched?.impuestos?.[index]?.total &&
                              errors?.impuestos?.[index]?.total
                                ? "!tw-border-danger"
                                : ""
                            }`}
                            value={values.impuestos[index].total}
                            onChange={(value) => {
                              const inputValue = Number.isNaN(value)
                                ? null
                                : value
                              inputValue &&
                                setFieldValue(
                                  `impuestos.${index}.total`,
                                  inputValue
                                )
                            }}
                            placeholder='0,00'
                            suffix='€'
                          />
                          <button
                            className='disabled:tw-cursor-not-allowed'
                            type='button'
                            disabled={values.impuestos?.length === 1}
                            onClick={() => removeImpuesto(index)}
                          >
                            <FontAwesomeIcon
                              className={`tw-text-danger tw-ml-6 tw-my-auto tw-h-5 tw-mr-2 ${
                                values.impuestos?.length === 1
                                  ? "tw-text-gray-500 tw-opacity-50"
                                  : ""
                              }`}
                              icon={["fal", "trash-can"]}
                            />
                          </button>
                        </div>
                        <ErrorMessage
                          name={`impuestos.${index}.total`}
                          component='div'
                          className='tw-text-danger tw-text-left'
                        />
                        <ErrorMessage
                          name='totalImpuestos'
                          component='div'
                          className='tw-text-danger tw-text-left'
                        />
                      </div>
                    </div>
                  </div>
                )
              )}
            <button
              type='button'
              className='!tw-p-2 !tw-bg-white !tw-rounded tw-shadow'
              onClick={() =>
                pushImpuesto({
                  baseImponible: null,
                  porcentaje: impuestoExento,
                  total: null,
                })
              }
            >
              <FontAwesomeIcon
                className='tw-text-primary tw-mr-3 tw-h-4'
                icon={["fal", "plus"]}
              />
              <span className='tw-pr-2'>{t("ocr.amount.taxes.add")}</span>
            </button>
          </div>
        )}
      </FieldArray>

      <p className='tw-text-left tw-text-neutral-75 tw-font-medium tw-text-base'>
        {t("ocr.amount.retentions.title")}
      </p>
      <FieldArray name='retencion'>
        {({ remove: removeRetencion, push: pushRetencion }) => (
          <div className='tw-bg-background tw-pb-6 tw-mb-6'>
            <div className='tw-grid tw-grid-cols-3 tw-gap-2 tw-bg-white tw-px-4 tw-mb-4'>
              <div className='tw-text-left tw-text-text-50 tw-text-base tw-font-medium tw-p-2'>
                {t("ocr.amount.retentions.base")}
              </div>
              <div className='tw-text-left tw-text-text-50 tw-text-base tw-font-medium tw-p-2'>
                {t("ocr.amount.retentions.percentage")}
              </div>
              <div className='tw-text-left tw-text-text-50 tw-text-base tw-font-medium tw-p-2'>
                {t("ocr.amount.retentions.total")}
              </div>
            </div>
            {values.retencion?.length > 0 &&
              values.retencion?.map(
                (
                  _impuesto: RetencionInsertFacturaOcrCommandProps,
                  index: number
                ) => (
                  <div
                    className='tw-bg-background tw-px-4 tw-pb-4'
                    key={index} // eslint-disable-line
                  >
                    <div className='tw-grid tw-grid-cols-3 tw-gap-2 tw-bg-white tw-p-2'>
                      <div>
                        <InputCurrency
                          className={`tw-w-full ${
                            touched?.retencion?.[index]?.baseImponible &&
                            errors?.retencion?.[index]?.baseImponible
                              ? "!tw-border-danger"
                              : ""
                          }`}
                          value={values.retencion[index].baseImponible}
                          onChange={(value) => {
                            const inputValue = Number.isNaN(value)
                              ? null
                              : value
                            if (typeof inputValue === "number") {
                              setFieldValue(
                                `retencion.${index}.total`,
                                precisionNumber(
                                  (inputValue *
                                    Math.abs(
                                      values.retencion[index].porcentaje
                                        ?.valor ?? 0
                                    )) /
                                    100
                                )
                              )
                            }
                            setFieldValue(
                              `retencion.${index}.baseImponible`,
                              inputValue
                            )
                          }}
                          placeholder='0,00'
                          suffix='€'
                        />
                        <ErrorMessage
                          name={`retencion.${index}.baseImponible`}
                          component='div'
                          className='tw-text-danger tw-text-left'
                        />
                      </div>
                      <div>
                        <Dropdown
                          className={`tw-w-full tw-rounded-md tw-border-neutral-25 ${
                            touched?.retencion?.[index]?.porcentaje &&
                            errors?.retencion?.[index]?.porcentaje
                              ? "!tw-border-danger"
                              : ""
                          }`}
                          id='listaRetenciones'
                          name='listaRetenciones'
                          optionLabel='value.valorLabel'
                          options={values.listaRetenciones}
                          value={values.retencion[index].porcentaje}
                          valueTemplate={
                            values.retencion[index].porcentaje?.valorLabel
                          }
                          onChange={({ value }) => {
                            setFieldValue(
                              `retencion.${index}.porcentaje`,
                              value
                            )
                            setFieldValue(
                              `retencion.${index}.total`,
                              precisionNumber(
                                ((values.retencion[index].baseImponible ?? 0) *
                                  (Math.abs(value.valor) ?? 0)) /
                                  100
                              )
                            )
                          }}
                          placeholder={`${t("ocr.placeholderPorcentaje")}`}
                          emptyFilterMessage={t("ocr.emptyMsg")}
                          emptyMessage={t("ocr.emptyMsg")}
                          // TODO: OCR. Activate tax-list filter when OCR is ready
                          // filter
                        />
                        <ErrorMessage
                          name={`retencion.${index}.porcentaje.id`}
                          component='div'
                          className='tw-text-danger tw-text-left'
                        />
                      </div>
                      <div>
                        <div className='tw-flex'>
                          <InputCurrency
                            className={`tw-w-full ${
                              touched?.retencion?.[index]?.total &&
                              errors?.retencion?.[index]?.total
                                ? "!tw-border-danger"
                                : ""
                            }`}
                            value={
                              Number.isNaN(values.retencion[index].total)
                                ? 0
                                : values.retencion[index].total
                            }
                            onChange={(value) => {
                              const inputValue = Number.isNaN(value)
                                ? null
                                : value
                              setFieldValue(
                                `retencion.${index}.total`,
                                inputValue
                              )
                            }}
                            placeholder='0,00'
                            suffix='€'
                          />
                          {/* TODO: OCR. Array is supported, just remove and uncomment when OCR is ready */}
                          <button
                            type='button'
                            className='disabled:tw-cursor-not-allowed'
                            onClick={() => removeRetencion(index)}
                            // disabled={values.retencion?.length === 1}
                          >
                            <FontAwesomeIcon
                              className='tw-text-danger tw-ml-6 tw-my-auto tw-h-5 tw-mr-2'
                              // className={`tw-text-danger tw-ml-6 tw-my-auto tw-h-5 tw-mr-2 ${
                              //   values.retencion?.length === 1
                              //     ? "tw-text-gray-500 tw-opacity-50"
                              //     : ""
                              // }`}
                              icon={["fal", "trash-can"]}
                            />
                          </button>
                        </div>
                        <ErrorMessage
                          name={`retencion.${index}.total`}
                          component='div'
                          className='tw-text-danger tw-text-left'
                        />
                      </div>
                    </div>
                  </div>
                )
              )}
            {/* TODO: OCR. Array is supported, just replace <1 and uncomment this when OCR is ready */}
            {values.retencion?.length < 1 && (
              <button
                type='button'
                className='!tw-p-2 !tw-bg-white !tw-rounded tw-shadow'
                onClick={() =>
                  pushRetencion({
                    baseImponible: undefined,
                    // El porcentaje impuesto tiene 2 valores: id para el back y valor para el front (se valida el id)
                    porcentaje: { id: undefined, valor: undefined },
                    total: undefined,
                  })
                }
              >
                <FontAwesomeIcon
                  className='tw-text-primary tw-mr-3 tw-h-4'
                  icon={["fal", "plus"]}
                />
                <span className='tw-pr-2'>
                  {t("ocr.amount.retentions.add")}
                </span>
              </button>
            )}
          </div>
        )}
      </FieldArray>
      <hr className='tw-mb-6' />

      <section className='tw-w-full tw-h-fit tw-grid tw-grid-cols-2 tw-gap-2'>
        {/* 
        //TODO: Descomentar si en algun momento se pueden usar divisas diferentes al euro.
        <div className='text-left p-fluid p-formgrid p-grid'>
          <div className='p-field p-col'>
            <label htmlFor='totalRetencion'>{t("ocr.currencyLabel")}</label>
            <Dropdown
              filter
              options={currencyListFormatDropdown}
              value={values.divisa}
              onChange={({ value }) => setFieldValue("divisa", value)}
              name='divisa'
              // @ts-expect-error TS(2769): No overload matches this call.
              placeholder={t("ocr.placeholderSearch")}
              emptyFilterMessage={t("ocr.emptyMsg")}
              emptyMessage={t("ocr.emptyMsg")}
              fluid
            />
          </div>
        </div> */}

        <div className='tw-flex tw-flex-col tw-justify-start tw-items-start'>
          <label htmlFor='totalRetencion'>{t("Total base imponible")}</label>
          <InputNumber
            className='tw-w-full [&>:first-child]:tw-text-right'
            id='totalRetencion'
            name='totalRetencion'
            placeholder='0,00 €'
            mode='currency'
            value={
              Number.isNaN(values.totalRetencion) ? 0 : values.totalRetencion
            }
            maxFractionDigits={2}
            minFractionDigits={2}
            currency={divisa || DEFAULT_CURRENCY_ABREVIATURE}
            onBlur={handleBlur}
            onChange={(e) => {
              setFieldValue("totalRetencion", e.value)
            }}
            disabled
          />
        </div>

        <div className='tw-flex tw-flex-col tw-justify-start tw-items-start'>
          <label htmlFor='total'>{t("ocr.importTotal")}</label>
          <InputCurrency
            className='tw-w-full'
            value={values.total}
            onChange={(value) => {
              const inputValue = Number.isNaN(value) ? null : value
              setFieldValue(`total`, inputValue)
            }}
            placeholder='0,00'
            suffix='€'
          />
        </div>
      </section>
    </>
  )
}

export default ImporteForm
